/* ===== Constructor de packs ===== */
(() => {
  if (window.BundleBuilderLoaded) return;
  window.BundleBuilderLoaded = true;

  const STORAGE_TTL = 1000 * 60 * 60 * 24 * 3; // 3 días
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /** "2:5, 3:10, 4:12,5" -> [{qty, percent}] (misma lógica que la función de descuento) */
  function parseTiers(text) {
    const tiers = [];
    const pattern = /(\d+)\s*[:=]\s*(\d+(?:[.,]\d+(?!\s*[:=]))?)/g;
    let match;
    while ((match = pattern.exec(text || '')) !== null) {
      const qty = parseInt(match[1], 10);
      const percent = parseFloat(match[2].replace(',', '.'));
      if (qty > 0 && percent > 0 && percent <= 100) tiers.push({ qty, percent });
    }
    return tiers.sort((a, b) => a.qty - b.qty);
  }

  function formatMoney(cents, format) {
    const value = Number(cents) || 0;
    const clean = String(format || '{{amount}}').replace(/<[^>]*>/g, '');
    const fmt = (decimals, thousands = ',', decimal = '.') => {
      const [int, dec] = (value / 100).toFixed(decimals).split('.');
      const intStr = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? intStr + decimal + dec : intStr;
    };
    return clean.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(0);
        case 'amount_with_comma_separator': return fmt(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(2, "'", '.');
        case 'amount_with_space_separator': return fmt(2, ' ', ',');
        default: return fmt(2);
      }
    });
  }

  const formatPercent = (p) => (Number.isInteger(p) ? String(p) : String(p).replace('.', ','));
  const fill = (tpl, values) =>
    String(tpl || '').replace(/\[(\w+)\]/g, (_, k) => (k in values ? values[k] : ''));

  class BundleBuilder {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(root.querySelector('[data-bb-config]').textContent);
      this.t = this.config.texts || {};
      this.tiers = parseTiers(this.config.tiers);
      this.visibleSlots = Math.max(1, parseInt(this.config.visibleSlots, 10) || 6);
      this.maxPerProduct = parseInt(this.config.maxPerProduct, 10) || 0;
      this.storageKey = `bundle-builder:${this.config.pack}`;
      this.busy = false;

      const q = (sel) => root.querySelector(sel);
      this.els = {
        panel: q('[data-bb-panel]'),
        toggle: q('[data-bb-toggle]'),
        count: q('[data-bb-count]'),
        message: q('[data-bb-message]'),
        slots: q('[data-bb-slots]'),
        discount: q('[data-bb-discount]'),
        discountLabel: q('[data-bb-discount-label]'),
        discountAmount: q('[data-bb-discount-amount]'),
        cta: q('[data-bb-cta]'),
        error: q('[data-bb-error]'),
        tiersList: q('[data-bb-tiers-list]'),
      };

      this.variants = new Map();
      this.cards = [...root.querySelectorAll('[data-bb-card]')];
      this.cards.forEach((card) => this.setupCard(card));

      this.items = this.load()
        .filter((i) => this.variants.has(i.variantId))
        .map((i) => ({ ...i, qty: Math.min(i.qty, this.variants.get(i.variantId).max) }))
        .filter((i) => i.qty > 0);

      this.bindFilters();
      this.bindPanel();
      this.bindFaqLink();
      this.render();
    }

    /* ---------- Tarjetas ---------- */
    setupCard(card) {
      const productId = card.dataset.productId;
      const title = card.dataset.title || '';
      const productImage = card.dataset.image || '';
      const onAddMode = card.dataset.sizeMode === 'on_add';
      const sizes = [...card.querySelectorAll('[data-bb-size]')];
      const isSingle = sizes.length === 1 && sizes[0].hasAttribute('data-default');

      sizes.forEach((el) => {
        const max = parseInt(el.dataset.max, 10);
        this.variants.set(el.dataset.variantId, {
          id: el.dataset.variantId,
          productId,
          title,
          label: el.dataset.label || '',
          price: parseInt(el.dataset.price, 10) || 0,
          max: Number.isFinite(max) ? Math.max(0, max) : Infinity,
          image: el.dataset.image || productImage,
          card,
        });
        if (el.tagName === 'BUTTON') {
          el.addEventListener('click', () => {
            if (el.disabled) return;
            this.selectSize(card, el);
            if (onAddMode) {
              this.add(el.dataset.variantId);
              this.closePicker(card);
            }
          });
        }
      });

      // Desplegable de tallas (móvil, modo visible)
      const select = card.querySelector('[data-bb-size-select]');
      if (select) {
        select.addEventListener('change', () => {
          const btn = card.querySelector(`button[data-bb-size][data-variant-id="${select.value}"]`);
          if (btn) {
            this.selectSize(card, btn);
          } else {
            card.bbSelected = null;
            this.updateCard(card);
          }
        });
      }

      card.bbSelected = isSingle ? sizes[0].dataset.variantId : null;

      card.querySelector('[data-bb-add]')?.addEventListener('click', () => {
        if (onAddMode && !isSingle) {
          this.openPicker(card);
          return;
        }
        this.onAdd(card);
      });
      card.querySelector('[data-bb-inc]')?.addEventListener('click', () => {
        if (onAddMode && !isSingle) {
          this.openPicker(card);
          return;
        }
        this.onAdd(card);
      });
      card.querySelector('[data-bb-dec]')?.addEventListener('click', () => {
        if (onAddMode && !isSingle) {
          this.removeLastOfProduct(card);
          return;
        }
        if (card.bbSelected) this.remove(card.bbSelected);
      });
      card.querySelector('[data-bb-quickview]')?.addEventListener('click', () => this.openQuickView(card));

      // Panel de tallas (modo "al pulsar +")
      card.querySelector('[data-bb-picker-close]')?.addEventListener('click', () => this.closePicker(card));
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closePicker(card);
      });
    }

    openPicker(card) {
      const picker = card.querySelector('[data-bb-picker]');
      if (!picker) {
        this.onAdd(card);
        return;
      }
      this.cards.forEach((c) => {
        if (c !== card) this.closePicker(c);
      });
      picker.hidden = false;
      card.classList.add('is-picking');
      picker.querySelector('button[data-bb-size]:not(:disabled)')?.focus();
    }

    closePicker(card) {
      const picker = card.querySelector('[data-bb-picker]');
      if (!picker || picker.hidden) return;
      picker.hidden = true;
      card.classList.remove('is-picking');
      card.querySelector('[data-bb-add]:not([hidden]), [data-bb-inc]')?.focus();
    }
        removeLastOfProduct(card) {
      const productId = card.dataset.productId;
      if (card.bbSelected && this.qtyOf(card.bbSelected) > 0) {
        this.remove(card.bbSelected);
        return;
      }
      const last = [...this.items]
        .reverse()
        .find((i) => this.variants.get(i.variantId)?.productId === productId);
      if (last) this.remove(last.variantId);
    }

    selectSize(card, el) {
      if (el.disabled) return;
      card.bbSelected = el.dataset.variantId;
      card.querySelectorAll('button[data-bb-size]').forEach((b) => {
        const active = b === el;
        b.classList.toggle('is-selected', active);
        b.setAttribute('aria-pressed', String(active));
      });
      const select = card.querySelector('[data-bb-size-select]');
      if (select && select.value !== el.dataset.variantId) select.value = el.dataset.variantId;
      card.classList.remove('is-alert');
      const hint = card.querySelector('[data-bb-hint]');
      if (hint) hint.textContent = '';
      this.updateCard(card);
    }

    onAdd(card) {
      if (!card.bbSelected) {
        card.classList.add('is-alert');
        const hint = card.querySelector('[data-bb-hint]');
        if (hint) hint.textContent = this.t.chooseSize || '';
        const select = card.querySelector('[data-bb-size-select]');
        if (select && select.offsetParent !== null) select.focus();
        else card.querySelector('button[data-bb-size]:not(:disabled)')?.focus();
        return;
      }
      this.add(card.bbSelected);
    }

    updateCard(card) {
      const productId = card.dataset.productId;
      const onAddMode = card.dataset.sizeMode === 'on_add';
      const selectedQty = this.qtyOf(card.bbSelected);
      const productQty = this.productQty(productId);

      const add = card.querySelector('[data-bb-add]');
      const stepper = card.querySelector('[data-bb-stepper]');
      const qtyEl = card.querySelector('[data-bb-qty]');

      // Tallas ocultas: el selector muestra el total del producto (todas las tallas)
      const showStepper = onAddMode ? productQty > 0 : Boolean(card.bbSelected) && selectedQty > 0;
      const shownQty = onAddMode ? productQty : selectedQty;

      if (add) add.hidden = showStepper;
      if (stepper) stepper.hidden = !showStepper;
      if (qtyEl) qtyEl.textContent = String(shownQty);
      card.classList.toggle('is-in-pack', productQty > 0);

      card.querySelectorAll('button[data-bb-size]').forEach((b) => {
        b.classList.toggle('has-qty', this.qtyOf(b.dataset.variantId) > 0);
      });
    }

    /* ---------- Estado del pack ---------- */
    qtyOf(variantId) {
      const item = this.items.find((i) => i.variantId === variantId);
      return item ? item.qty : 0;
    }

    productQty(productId) {
      return this.items.reduce(
        (sum, i) => sum + (this.variants.get(i.variantId)?.productId === productId ? i.qty : 0),
        0,
      );
    }

    add(variantId) {
      const variant = this.variants.get(variantId);
      if (!variant) return;
      const current = this.qtyOf(variantId);
      if (current + 1 > variant.max) return this.flash(this.t.noStock);
      if (this.maxPerProduct > 0 && this.productQty(variant.productId) + 1 > this.maxPerProduct) {
        return this.flash(fill(this.t.maxProduct, { count: this.maxPerProduct }));
      }
      const item = this.items.find((i) => i.variantId === variantId);
      if (item) item.qty += 1;
      else this.items.push({ variantId, qty: 1 });
      this.commit();
    }

    remove(variantId) {
      const item = this.items.find((i) => i.variantId === variantId);
      if (!item) return;
      item.qty -= 1;
      this.items = this.items.filter((i) => i.qty > 0);
      this.commit();
    }

    commit() {
      this.save();
      this.render();
    }

    load() {
      try {
        const raw = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
        if (!raw || Date.now() - raw.t > STORAGE_TTL) return [];
        return (raw.items || [])
          .map((i) => ({ variantId: String(i.variantId), qty: parseInt(i.qty, 10) || 0 }))
          .filter((i) => i.qty > 0);
      } catch (e) {
        return [];
      }
    }

    save() {
      try {
        if (this.items.length) {
          localStorage.setItem(this.storageKey, JSON.stringify({ t: Date.now(), items: this.items }));
        } else {
          localStorage.removeItem(this.storageKey);
        }
      } catch (e) {
        /* almacenamiento no disponible: el pack funciona igual, solo no se recuerda */
      }
    }

    /* ---------- Render ---------- */
    render() {
      const units = [];
      this.items.forEach((item) => {
        const v = this.variants.get(item.variantId);
        for (let i = 0; i < item.qty; i++) units.push(v);
      });

      const total = units.length;
      const subtotal = units.reduce((sum, v) => sum + v.price, 0);
      const current = [...this.tiers].reverse().find((t) => total >= t.qty) || null;
      const next = this.tiers.find((t) => t.qty > total) || null;
      const discount = current
        ? this.items.reduce((sum, i) => {
            const v = this.variants.get(i.variantId);
            return sum + Math.round((v.price * i.qty * current.percent) / 100);
          }, 0)
        : 0;
      const money = (c) => formatMoney(c, this.config.moneyFormat);

      if (this.els.count) this.els.count.textContent = fill(this.t.count, { count: total });

      let message;
      if (total === 0) message = this.t.empty;
      else if (next) message = fill(this.t.progress, { count: next.qty - total, percent: formatPercent(next.percent) });
      else if (current) message = fill(this.t.max, { percent: formatPercent(current.percent) });
      else message = '';
      this.els.message.textContent = message || '';

      this.renderTiers(current);
      this.renderSlots(units);

      if (current && discount > 0) {
        this.els.discount.hidden = false;
        this.els.discountLabel.textContent = fill(this.t.discount, { percent: formatPercent(current.percent) });
        this.els.discountAmount.textContent = `-${money(discount)}`;
      } else {
        this.els.discount.hidden = true;
      }

      const cta = this.els.cta;
      cta.replaceChildren();
      const minQty = this.tiers.length ? this.tiers[0].qty : 1;
      if (this.busy) {
        cta.disabled = true;
        cta.textContent = this.t.adding || '';
      } else if (total === 0) {
        cta.disabled = true;
        cta.textContent = fill(this.t.ctaLocked, { count: minQty });
      } else {
        cta.disabled = total < minQty && !this.config.allowBelowMin;
        const label = document.createElement('span');
        label.textContent = this.t.cta || '';
        const price = document.createElement('strong');
        price.textContent = `| ${money(subtotal - discount)}`;
        cta.append(label, price);
        if (discount > 0) {
          const old = document.createElement('s');
          old.textContent = money(subtotal);
          cta.append(old);
        }
      }

      this.cards.forEach((card) => this.updateCard(card));
    }

    renderTiers(current) {
      const list = this.els.tiersList;
      if (!list) return;

      if (!list.children.length) {
        let prev = null;
        this.tiers.forEach((tier) => {
          if (prev && prev.percent === tier.percent) return; // tramos repetidos: solo una vez
          const li = document.createElement('li');
          li.className = 'bb__tier';
          li.dataset.percent = String(tier.percent);
          const off = document.createElement('strong');
          off.textContent = fill(this.t.tierOff, { percent: formatPercent(tier.percent) });
          const qty = document.createElement('span');
          qty.textContent = fill(this.t.tierQty, { count: tier.qty });
          li.append(off, qty);
          list.append(li);
          prev = tier;
        });
      }

      [...list.children].forEach((li) => {
        li.classList.toggle('is-active', Boolean(current) && Number(li.dataset.percent) === current.percent);
      });
    }

    renderSlots(units) {
      const frag = document.createDocumentFragment();
      const slotCount = units.length >= this.visibleSlots ? units.length + 1 : this.visibleSlots;

      for (let i = 0; i < slotCount; i++) {
        const pos = i + 1;
        const v = units[i];
        const slot = document.createElement('div');
        slot.className = 'bb-slot';

        if (v) {
          slot.classList.add('bb-slot--filled');
          if (v.image) {
            const img = document.createElement('img');
            img.className = 'bb-slot__img';
            img.src = v.image;
            img.alt = '';
            img.loading = 'lazy';
            slot.append(img);
          }
          const name = document.createElement('span');
          name.className = 'bb-slot__name';
          name.textContent = v.title;
          slot.append(name);
          if (v.label) {
            const size = document.createElement('span');
            size.className = 'bb-slot__size';
            size.textContent = v.label;
            slot.append(size);
          }
          const removeBtn = document.createElement('button');
          removeBtn.type = 'button';
          removeBtn.className = 'bb-slot__remove';
          removeBtn.setAttribute('aria-label', `${this.t.removeAria || ''} ${v.title}`.trim());
          removeBtn.textContent = '✕';
          removeBtn.addEventListener('click', () => this.remove(v.id));
          slot.append(removeBtn);
        } else {
          const num = document.createElement('span');
          num.className = 'bb-slot__num';
          num.textContent = String(pos);
          slot.append(num);

          const tier = this.tiers.find((t) => t.qty === pos);
          if (tier) {
            slot.classList.add('bb-slot--tier');
            const off = document.createElement('span');
            off.className = 'bb-slot__off';
            off.textContent = fill(this.t.slotOff, { percent: formatPercent(tier.percent) });
            slot.append(off);
          }
          const label = document.createElement('span');
          label.className = 'bb-slot__label';
          label.textContent = pos > this.visibleSlots ? this.t.slotMore : this.t.slotEmpty;
          slot.append(label);
        }
        frag.append(slot);
      }
      this.els.slots.replaceChildren(frag);
    }

    flash(text, type = 'error') {
      if (!text || !this.els.error) return;
      this.els.error.textContent = text;
      this.els.error.dataset.type = type;
      this.els.error.hidden = false;
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => {
        this.els.error.hidden = true;
      }, 3500);
    }

    /* ---------- Añadir al carrito ---------- */
    async addToCart() {
      if (this.busy || !this.items.length) return;
      this.busy = true;
      this.render();

      let added = false;
      try {
        const res = await fetch(`${ROOT}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: this.items.map((i) => ({
              id: Number(i.variantId),
              quantity: i.qty,
              properties: { _bundle: this.config.pack },
            })),
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || this.t.error);

        this.items = [];
        this.save();
        added = true;
        document.dispatchEvent(new CustomEvent('bundle-builder:added', { detail: data }));
      } catch (e) {
        this.flash((e && e.message) || this.t.error);
      } finally {
        this.busy = false;
        this.render();
      }

      if (added) this.afterAdd();
    }

    afterAdd() {
      const mode = this.config.afterAdd;

      if (mode === 'cart') {
        window.location.href = `${ROOT}cart`;
        return;
      }

      if (mode === 'drawer') {
        try {
          if (window.Rebuy && window.Rebuy.Cart && window.Rebuy.Cart.fetchCart) window.Rebuy.Cart.fetchCart();
          if (window.Rebuy && window.Rebuy.SmartCart && window.Rebuy.SmartCart.show) window.Rebuy.SmartCart.show();
        } catch (e) {
        }
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }

      this.flash(this.t.added, 'success');
    }
    /* ---------- Vista rápida ---------- */
    openQuickView(card) {
      if (window.BundleQuickView && window.BundleQuickView.open) {
        window.BundleQuickView.open(card, this);
      } else {
        window.location.href = `${ROOT}products/${card.dataset.handle}`;
      }
    }

    /* ---------- Filtros, panel y FAQs ---------- */
    bindFilters() {
      const bar = this.root.querySelector('[data-bb-filters]');
      const buttons = [...this.root.querySelectorAll('[data-bb-filter]')];

      buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const handle = btn.dataset.bbFilter;

          buttons.forEach((b) => {
            const active = b === btn;
            b.classList.toggle('is-active', active);
            b.setAttribute('aria-pressed', String(active));
          });

          this.cards.forEach((card) => {
            card.hidden = card.dataset.filter !== handle;
          });

          if (bar) {
            const left = btn.offsetLeft - (bar.clientWidth - btn.clientWidth) / 2;
            bar.scrollTo({ left, behavior: 'smooth' });
          }
        });
      });
    }

    bindPanel() {
      this.els.cta.addEventListener('click', () => this.addToCart());
      this.els.toggle?.addEventListener('click', () => {
        const expanded = this.els.panel.classList.toggle('is-expanded');
        this.els.toggle.setAttribute('aria-expanded', String(expanded));
      });
    }

    bindFaqLink() {
      this.root.querySelectorAll('a[href="#faqs"]').forEach((link) => {
        link.addEventListener('click', (e) => {
          const target = document.querySelector('.accordion-group');
          if (!target) return;
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    }
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-bundle-builder]').forEach((el) => {
      if (!el.bbInstance) el.bbInstance = new BundleBuilder(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();